import { Box, Typography } from '@mui/material';
import React, { FC } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';

export type EngagementBarChartComponentProps = {
	data?: any;
};
export const EngagementBarChartComponent: FC<EngagementBarChartComponentProps> = (props) => {
	const { data } = props;
	const uniqueYears = new Set(data?.map((item: any) => new Date(item._id.monthStart).getFullYear()));
	const showYear = uniqueYears.size > 1;
	const grouped: any = {};

	data?.forEach((item: any) => {
		const { _id, count } = item;
		const date = new Date(_id.monthStart);
		const label = date.toLocaleString('eng', {
			month: 'short',
			...(showYear && { year: 'numeric' }),
		});

		if (!grouped[label]) grouped[label] = { month: label };

		grouped[label]['count'] = count;
	});
	const finalChartData = Object.values(grouped);

	const CustomTooltip = (props: any) => {
		const { active, payload, label } = props;

		if (!active || !payload || !payload.length) return null;

		return (
			<Box
				sx={{
					width: '100px',
					maxHeight: '170px',
					overflowY: 'auto',
					backgroundColor: 'white',
					border: '1px solid #ccc',
					borderRadius: '6px',
					padding: '10px',
					boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
					pointerEvents: 'auto',
					py: 2,
					px: 2,
				}}
			>
				<Typography sx={{ mb: '10px', fontWeight: 'bold', mt: 0 }}>{label}</Typography>
				{payload.map((entry: any, index: number) => (
					<Box
						key={index}
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							gap: '8px',
						}}
					>
						<Box
							component="span"
							sx={{
								flex: 1,
								color: entry.color,
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							}}
							title={entry.name}
						>
							{entry.name}
						</Box>
						<Box component="span" sx={{ whiteSpace: 'nowrap' }}>
							{entry.value}
						</Box>
					</Box>
				))}
			</Box>
		);
	};

	return (
		<ResponsiveContainer width="100%" height="100%">
			<BarChart data={finalChartData?.length > 0 ? finalChartData : [{ x: '', y: 0 }]}>
				<CartesianGrid strokeDasharray="4 4" y={20} syncWithTicks={true} />
				<XAxis
					dataKey="month"
					tickSize={12}
					tick={({ x, y, payload, textAnchor, ...rest }) => (
						<text x={x} y={y + 12} orientation={'bottom'} textAnchor={'middle'} {...rest}>
							<tspan fontSize={12}>{payload.value}</tspan>
						</text>
					)}
				/>
				<YAxis
					allowDecimals={false}
					tick={({ x, y, payload, textAnchor, ...rest }) => (
						<text x={x} y={y + 5} textAnchor={'end'} {...rest} fill="#F89377">
							<tspan fontSize={12}>{payload.value}</tspan>
						</text>
					)}
					padding={{ top: 20 }}
					tickSize={12}
				/>
				<Tooltip content={CustomTooltip} cursor={{ fill: 'transparent' }} />
				<Bar dataKey="count" fill="#78BBE2" radius={[10, 10, 0, 0]} barSize={50} />
			</BarChart>
		</ResponsiveContainer>
	);
};
